/**
 * Provider Type Helper Utilities
 * 
 * Centralized provider type constants and helper functions for the admin frontend.
 * Matches the mobile app's provider type system for consistency.
 * 
 * @module ProviderTypeHelpers
 */

/**
 * Provider Type Definitions with visual styling
 * Aligned with Role Templates in integratedPermissions.js
 */
export const PROVIDER_TYPES = {
  // ========================================
  // CLINICAL ROLES - Licensed professionals
  // ========================================
  MHP: {
    bundles: ['behavioral-health'],
    value: 'MHP',
    label: 'Mental Health Practitioner',
    emoji: '🧠',
    color: '#8B5CF6', // Purple
    bgColor: '#CCFBF1',
    textColor: '#134E4A',
    category: 'clinical',
    description: 'Licensed mental health practitioner (LMHP, LICSW, etc.)'
  },
  RN: {
    // 144G assisted living requires licensed nursing (144G.60 / 144G.71).
    bundles: ['home-health', 'residential-services'],
    value: 'RN',
    label: 'Registered Nurse',
    emoji: '💉',
    color: '#14B8A6', // Teal
    bgColor: '#F0FDFA',
    textColor: '#115E59',
    category: 'nursing',
    description: 'Registered Nurse with state license'
  },
  LPN: {
    // 144G assisted living requires licensed nursing (144G.60 / 144G.71).
    bundles: ['home-health', 'residential-services'],
    value: 'LPN',
    label: 'Licensed Practical Nurse',
    emoji: '🏥',
    color: '#06B6D4', // Cyan
    bgColor: '#ECFEFF',
    textColor: '#164E63',
    category: 'nursing',
    description: 'Licensed Practical Nurse'
  },
  Therapist: {
    bundles: ['behavioral-health'],
    value: 'Therapist',
    label: 'Therapist',
    emoji: '🩺',
    color: '#EC4899', // Pink
    bgColor: '#FFF7ED',
    textColor: '#9A3412',
    category: 'clinical',
    description: 'Licensed therapist (LMFT, LPCC, LCSW, etc.)'
  },
  Clinical_Supervisor: {
    bundles: ['behavioral-health'],
    value: 'Clinical_Supervisor',
    label: 'Clinical Supervisor',
    emoji: '👔',
    color: '#F59E0B', // Amber
    bgColor: '#FFFBEB',
    textColor: '#92400E',
    category: 'clinical',
    description: 'Licensed clinical supervisor'
  },
  MH_Supervisor: {
    bundles: ['behavioral-health', 'mh-co-responder'],
    value: 'MH_Supervisor',
    label: 'MH Supervisor',
    emoji: '🧑‍💼',
    color: '#D97706', // Amber-600
    bgColor: '#FEF3C7',
    textColor: '#92400E',
    category: 'clinical',
    description: 'Mental health supervisor'
  },

  // ========================================
  // DIRECT CARE ROLES - Non-licensed staff
  // ========================================
  Caregiver: {
    value: 'Caregiver',
    label: 'Provider',
    emoji: '👨‍⚕️',
    color: '#10B981', // Green
    bgColor: '#ECFDF5',
    textColor: '#065F46',
    category: 'direct_care',
    description: 'General provider delivering direct care'
  },
  PCA: {
    bundles: ['home-health'],
    value: 'PCA',
    label: 'Personal Care Assistant',
    emoji: '🤲',
    color: '#22C55E', // Light Green
    bgColor: '#F0FDF4',
    textColor: '#166534',
    category: 'direct_care',
    description: 'Personal care assistant for daily living activities'
  },
  DSP: {
    bundles: ['245d-waiver-services'],
    value: 'DSP',
    label: 'Direct Support Professional (245D)',
    emoji: '🤝',
    color: '#0D9488', // Teal
    bgColor: '#F0FDFA',
    textColor: '#115E59',
    category: 'direct_care',
    description: '245D direct support for assigned clients: service notes and visits'
  },
  
  // ========================================
  // SUPPORT ROLES
  // ========================================
  Coordinator: {
    value: 'Coordinator',
    label: 'Care Coordinator',
    emoji: '📅',
    color: '#84CC16', // Lime
    bgColor: '#F7FEE7',
    textColor: '#3F6212',
    category: 'administrative',
    description: 'Care coordination and scheduling'
  },
  Case_Manager: {
    value: 'Case_Manager',
    label: 'Case Manager',
    emoji: '📋',
    color: '#6366F1', // Indigo
    bgColor: '#F1F5F9',
    textColor: '#334155',
    category: 'administrative',
    description: 'Case management and service coordination'
  },
  // MH-TCM case manager associate (MHCP): offered to organizations running
  // Case Management. Same badge palette as Case Manager.
  Case_Manager_Associate: {
    bundles: ['case-management'],
    value: 'Case_Manager_Associate',
    label: 'Case Manager Associate',
    emoji: '📋',
    color: '#6366F1', // guard-ok: ui/hardcoded-hex — badge palette table, same as Case_Manager
    bgColor: '#F1F5F9', // guard-ok: ui/hardcoded-hex — badge palette table, same as Case_Manager
    textColor: '#334155', // guard-ok: ui/hardcoded-hex — badge palette table, same as Case_Manager
    category: 'administrative',
    description: 'Case management associate working under a case manager (Rule 79 training and clinical supervision)'
  },
  Social_Worker: {
    value: 'Social_Worker',
    label: 'Social Worker',
    emoji: '🤝',
    color: '#8B5CF6', // Purple
    bgColor: '#DBEAFE',
    textColor: '#1E3A8A',
    category: 'administrative',
    description: 'Licensed social worker (LSW, LICSW, etc.)'
  },
  Driver: {
    bundles: ['adult-day-services'],
    value: 'Driver',
    label: 'Driver',
    emoji: '🚐',
    color: '#3B82F6', // Blue
    bgColor: '#EFF6FF',
    textColor: '#1E40AF',
    category: 'transportation',
    description: 'Transportation provider'
  },
  ADS_Supervisor: {
    bundles: ['adult-day-services'],
    value: 'ADS_Supervisor',
    label: 'ADS Supervisor',
    emoji: '👨‍✈️',
    color: '#0EA5E9', // Sky Blue
    bgColor: '#F0F9FF',
    textColor: '#075985',
    category: 'transportation',
    description: 'Adult Day Services transportation supervisor - manages drivers and client assignments'
  },
  Activity_Assistant: {
    bundles: ['adult-day-services'],
    value: 'Activity_Assistant',
    label: 'Activity Assistant',
    emoji: '🎯',
    color: '#0891B2', // Cyan-600
    bgColor: '#ECFEFF',
    textColor: '#155E75',
    category: 'transportation',
    description: 'Adult Day Services non-driver staff supporting clients during day program activities'
  },
  Billing_Specialist: {
    bundles: ['billing'],
    value: 'Billing_Specialist',
    label: 'Billing Specialist',
    emoji: '�',
    color: '#EAB308', // Yellow
    bgColor: '#FEF9C3',
    textColor: '#854D0E',
    category: 'administrative',
    description: 'Billing and claims specialist'
  },

  // Historical SCPD records used this value before the first-class MHRU types
  // were introduced. It is display/edit compatibility only: new assignments
  // must use one of the policy-controlled types below.
  Co_Responder: {
    bundles: ['mh-co-responder'],
    value: 'Co_Responder',
    label: 'Co-Responder (legacy)',
    emoji: '🛡️',
    color: '#64748B',
    bgColor: '#F1F5F9',
    textColor: '#334155',
    category: 'clinical',
    legacy: true,
    description: 'Legacy Mental Health Co-Responder assignment'
  },

  // ========================================
  // CO-RESPONDER (MHRU) MEMBER ROLES
  // Org-scoped: only shown to organizations granted the
  // `co_responder_provider_types` entitlement (see orgFeatures.js). The
  // `feature` marker below is what filters these out of every other tenant's
  // picker (including WeCare). Colors reuse the existing in-file palette rather
  // than introducing new hex values.
  // ========================================
  Clinician_Co_Responder: {
    bundles: ['mh-co-responder'],
    value: 'Clinician_Co_Responder',
    label: 'Clinician Co-Responder',
    emoji: '🧠',
    color: '#14B8A6', // Teal (reused)
    bgColor: '#F0FDFA',
    textColor: '#115E59',
    category: 'clinical',
    feature: 'co_responder_provider_types',
    description: 'Mental Health Response Unit clinician co-responder'
  },
  Law_Enforcement_Officer: {
    bundles: ['mh-co-responder'],
    value: 'Law_Enforcement_Officer',
    label: 'Law Enforcement Officer',
    emoji: '🚓',
    color: '#3B82F6', // Blue (reused)
    bgColor: '#EFF6FF',
    textColor: '#1E40AF',
    category: 'clinical',
    feature: 'co_responder_provider_types',
    description: 'Law enforcement officer on the Mental Health Response Unit'
  },
  Community_Paramedic: {
    bundles: ['mh-co-responder'],
    value: 'Community_Paramedic',
    label: 'Community Paramedic',
    emoji: '🚑',
    color: '#10B981', // Green (reused)
    bgColor: '#ECFDF5',
    textColor: '#065F46',
    category: 'clinical',
    feature: 'co_responder_provider_types',
    description: 'Community paramedic on the Mental Health Response Unit'
  },
  Intern: {
    bundles: ['mh-co-responder'],
    value: 'Intern',
    label: 'Intern',
    emoji: '🎓',
    color: '#0EA5E9', // Sky (reused)
    bgColor: '#F0F9FF',
    textColor: '#075985',
    category: 'clinical',
    feature: 'co_responder_provider_types',
    description: 'Mental Health Response Unit intern'
  }
};

export const CLIENT_TIME_ATTESTATION_PROVIDER_TYPES = Object.freeze(['MHP', 'Therapist']);

export const canUseClientTimeAttestation = (providerType) =>
  CLIENT_TIME_ATTESTATION_PROVIDER_TYPES.includes(providerType);

/**
 * Provider types are offered based on what the organization actually runs.
 *
 * A `bundles` key means "only offer this type when the org holds one of these
 * bundles" — an adult-day-only tenant has no use for an RN, and a
 * behavioral-health-only tenant has no drivers. Types WITHOUT a `bundles` key
 * (Administrative, Coordinator, Case_Manager, Social_Worker, Caregiver,
 * Provider) are cross-modality and stay available to everyone: the same rule the
 * sidebar gating follows — gate on evidence of modality, never on a guess.
 *
 * Bundle recommendations are presentation-only. A platform administrator can
 * save an explicit organization policy; that policy is then enforced by the
 * server. Existing values remain editable even when they sit outside current
 * bundles or a later policy — see getSelectableProviderTypes.
 */

/**
 * Provider type values that are gated behind an organization entitlement.
 * Entries with a `feature` key are offered only when that organization holds
 * the entitlement. A saved platform policy may further narrow base types.
 */
export const FEATURE_GATED_PROVIDER_TYPES = Object.values(PROVIDER_TYPES)
  .filter((t) => !!t.feature)
  .map((t) => t.value);

export const PROVIDER_TYPE_POLICY_FEATURE = 'provider_types_configured';
export const PROVIDER_TYPE_FEATURE_PREFIX = 'provider_type:';

/**
 * Provider Category Definitions
 */
export const PROVIDER_CATEGORIES = {
  direct_care: {
    value: 'direct_care',
    label: 'Direct Care',
    description: 'Hands-on patient care providers',
    icon: '👨‍⚕️'
  },
  transportation: {
    value: 'transportation',
    label: 'Transportation',
    description: 'Patient transportation services',
    icon: '🚐'
  },
  clinical: {
    value: 'clinical',
    label: 'Clinical',
    description: 'Clinical and therapeutic services',
    icon: '🩺'
  },
  nursing: {
    value: 'nursing',
    label: 'Nursing',
    description: 'Nursing care and medical services',
    icon: '💉'
  },
  administrative: {
    value: 'administrative',
    label: 'Administrative',
    description: 'Case management and coordination',
    icon: '📋'
  }
};

/**
 * Get provider type badge information
 * @param {string} providerType - Provider type value
 * @returns {Object} Badge info with emoji, color, label
 */
/**
 * Inline style for a provider-type badge, in both themes.
 *
 * The palette is a light chip with dark text, which is right in light mode and
 * wrong in dark: a bright chip glares against a dark table, and the house rule
 * is a dark background with light text there.
 *
 * Rather than invent a second palette, the pair is INVERTED in dark mode: the
 * dark text colour becomes the background and the light background becomes the
 * text. The contrast ratio is identical by construction, so every badge keeps
 * the AA margin it already measured (6.8:1 to 9.5:1) with no new colour
 * decisions to get wrong. The swap itself lives in darkModeEnhanced.css,
 * keyed off these two custom properties.
 *
 * @param {{bgColor: string, textColor: string}} badge
 */
export const providerTypeBadgeStyle = (badge) => ({
  backgroundColor: badge.bgColor,
  color: badge.textColor,
  '--ptb-bg': badge.bgColor,
  '--ptb-fg': badge.textColor,
});

export const getProviderTypeBadge = (providerType) => {
  if (!providerType) {
    return {
      emoji: '❓',
      color: '#9CA3AF',
      bgColor: '#F3F4F6',
      textColor: '#4B5563',
      label: 'Not Set'
    };
  }
  
  const type = PROVIDER_TYPES[providerType];
  if (!type) {
    return {
      emoji: '❓',
      color: '#9CA3AF',
      bgColor: '#F3F4F6',
      textColor: '#4B5563',
      label: providerType
    };
  }
  
  return {
    emoji: type.emoji,
    color: type.color,
    bgColor: type.bgColor,
    textColor: type.textColor,
    label: type.label
  };
};

/**
 * Get provider type label for display
 * @param {Object} user - User object with providerType or role
 * @returns {string} Display label
 */
export const getProviderTypeLabel = (user) => {
  if (!user) return 'Unknown';
  
  // Check providerType first
  if (user.providerType) {
    const type = PROVIDER_TYPES[user.providerType];
    return type ? type.label : user.providerType;
  }
  
  // Fallback to role-based labels
  if (user.role === 'provider') return 'Personal Care Assistant';
  if (user.role === 'driver') return 'Driver';
  if (user.role === 'admin') return 'Administrator';
  if (user.role === 'super_admin') return 'System Administrator';
  
  return user.role || 'Unknown';
};

/**
 * Get provider category badge
 * @param {string} category - Provider category value
 * @returns {Object} Category info
 */
export const getProviderCategoryBadge = (category) => {
  if (!category) return { icon: '❓', label: 'Not Set' };
  
  const cat = PROVIDER_CATEGORIES[category];
  return cat ? { icon: cat.icon, label: cat.label } : { icon: '❓', label: category };
};

/**
 * Check if user should show provider-specific features (formerly caregiver features)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const shouldShowCaregiverFeatures = (user) => {
  if (!user) return false;
  return user.role === 'provider' || user.providerCategory === 'direct_care';
};

/**
 * Check if user should show driver-specific features
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const shouldShowDriverFeatures = (user) => {
  if (!user) return false;
  return user.role === 'driver' || user.providerCategory === 'transportation';
};

/**
 * Check if user should show clinical features
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const shouldShowClinicalFeatures = (user) => {
  if (!user) return false;
  return user.providerCategory === 'clinical';
};

/**
 * Check if user should show nursing features
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const shouldShowNursingFeatures = (user) => {
  if (!user) return false;
  return user.providerCategory === 'nursing';
};

/**
 * Get all provider types as array for dropdowns
 * @returns {Array} Array of provider type objects
 */
export const getProviderTypeOptions = () => {
  return Object.values(PROVIDER_TYPES).map(type => ({
    value: type.value,
    label: `${type.emoji} ${type.label}`,
    category: type.category
  }));
};

/**
 * Return the provider types this organization may SELECT, honoring
 * entitlement gating. Base types are always included; a type carrying a
 * `feature` marker is included only when that feature key is in the org's
 * enabled-feature list (from useOrgFeatures()). Use this to build any
 * provider-type <select>. Badge/label lookups should keep using the full
 * PROVIDER_TYPES map so existing users' badges always resolve.
 *
 * @param {string[]} [enabledFeatures] - org's enabled feature keys
 * @returns {Array} Array of provider type definition objects (same shape as PROVIDER_TYPES entries)
 */
export const getSelectableProviderTypes = (enabledFeatures = [], opts = {}) => {
  const features = Array.isArray(enabledFeatures) ? enabledFeatures : [];
  const { orgBundles = null, currentValue = null, bundlesKnown = false } = opts;
  const hasConfiguredPolicy = features.includes(PROVIDER_TYPE_POLICY_FEATURE);
  const configuredTypes = hasConfiguredPolicy
    ? new Set(features
      .filter((feature) => feature.startsWith(PROVIDER_TYPE_FEATURE_PREFIX))
      .map((feature) => feature.slice(PROVIDER_TYPE_FEATURE_PREFIX.length)))
    : null;

  // Bundle filtering is OPT-IN: callers that pass no orgBundles keep the previous
  // behaviour exactly, so existing pickers are unaffected.
  const held = Array.isArray(orgBundles) ? new Set(orgBundles) : null;
  // complete-care is a superset of the general bundles, mirroring
  // BundleService.hasBundle. It does not imply the co-responder vertical.
  const hasBundle = (slug) => held.has(slug) || held.has('complete-care');

  return Object.values(PROVIDER_TYPES).filter((type) => {
    // THE GUARD: the value this user already holds is always offered, even when
    // it sits outside the org's bundles. SCPD employs PCAs, Delight's own admin
    // is a PCA — dropping their current value would blank it on save.
    if (currentValue && type.value === currentValue) return true;

    if (type.legacy) return false;
    if (type.feature && !features.includes(type.feature)) return false;
    if (configuredTypes && !configuredTypes.has(type.value)) return false;
    if (!held || (!bundlesKnown && held.size === 0)) return true; // bundles unknown/not loaded
    if (!type.bundles) return true;              // cross-modality, always offered
    return type.bundles.some(hasBundle);
  });
};

/**
 * Get all provider categories as array for dropdowns
 * @returns {Array} Array of category objects
 */
export const getProviderCategoryOptions = () => {
  return Object.values(PROVIDER_CATEGORIES).map(cat => ({
    value: cat.value,
    label: `${cat.icon} ${cat.label}`,
    description: cat.description
  }));
};

/**
 * Get provider types by category
 * @param {string} category - Provider category
 * @returns {Array} Array of provider types in that category
 */
export const getProviderTypesByCategory = (category) => {
  if (!category) return [];
  
  return Object.values(PROVIDER_TYPES)
    .filter(type => type.category === category)
    .map(type => ({
      value: type.value,
      label: `${type.emoji} ${type.label}`
    }));
};

/**
 * Validate provider type and category match
 * @param {string} providerType - Provider type value
 * @param {string} providerCategory - Provider category value
 * @returns {boolean} True if they match
 */
export const validateProviderTypeCategory = (providerType, providerCategory) => {
  if (!providerType || !providerCategory) return false;
  
  const type = PROVIDER_TYPES[providerType];
  if (!type) return false;
  
  return type.category === providerCategory;
};

/**
 * Get suggested category for a provider type
 * @param {string} providerType - Provider type value
 * @returns {string|null} Suggested category value
 */
export const getSuggestedCategory = (providerType) => {
  if (!providerType) return null;
  
  const type = PROVIDER_TYPES[providerType];
  return type ? type.category : null;
};
